/**
 * breakdownRisk.js — 08/02/2026 (Q2 Lot A user prod)
 *
 * Helpers unifiés pour la gestion des pannes véhicules. Le user a demandé
 * une chaîne de causalité claire et cohérente entre l'état des véhicules,
 * la présence du staff garage, les fonctions support et les probabilités
 * d'événements mécaniques.
 *
 *   risque_panne = base × condition_véhicule × staff_garage × fonctions_support × fatigue
 *
 * Aucune dépendance circulaire — on ne dérive que de l'instance véhicule
 * et de l'état Zustand (lecture seule).
 */

/**
 * Code couleur de l'état d'un véhicule, basé sur son indicateur `wear`
 * (0-100). Plus le wear est élevé, plus le véhicule est usé.
 *
 *   wear < 30   → 'green'  (parfait état, risque -50 %)
 *   30 ≤ wear < 65 → 'orange' (état correct, risque +50 %)
 *   wear ≥ 65    → 'red'    (état dégradé, risque ×2,5)
 *
 * Ce mapping correspond à la proposition user (Vert 5% / Orange 15% / Rouge 25%)
 * une fois multiplié à la base proba mensuelle de chaque event.
 *
 * @param {object} vehicle
 * @returns {'green'|'orange'|'red'}
 */
export function getVehicleConditionColor(vehicle) {
  const wear = Math.max(0, Math.min(100, vehicle?.wear || 0));
  if (wear < 30) return 'green';
  if (wear < 65) return 'orange';
  return 'red';
}

const CONDITION_RISK_MULTIPLIER = {
  green: 0.5,
  orange: 1.5,
  red:    2.5,
};

/**
 * Multiplicateur de probabilité de panne pour un véhicule donné, prenant
 * en compte tous les facteurs métier. Retourne 1.0 = baseline neutre.
 *
 *   • État véhicule : 0.5 / 1.5 / 2.5 (selon wear).
 *   • Mehdi Ferhat recruté : ×0.85.
 *   • Bruno Lavergne recruté : ×0.75 (cumulatif avec Mehdi).
 *   • diag_preventif unlock : ×0.6 (déjà appliqué côté coreSlice mais on
 *     l'ajoute ici pour les callers qui font le calcul localement).
 *   • Fatigue chauffeur moyen > 70 : ×1.15.
 *
 * @param {object} state — Zustand state slice
 * @param {object} vehicle — instance véhicule (peut être null pour calcul moyen)
 */
export function computeBreakdownRiskMultiplier(state, vehicle = null) {
  let mult = 1.0;
  // 1) État véhicule (le facteur le + impactant)
  if (vehicle) {
    const color = getVehicleConditionColor(vehicle);
    mult *= CONDITION_RISK_MULTIPLIER[color];
  } else if (state?.vehicles?.length) {
    // Moyenne sur la flotte si pas de véhicule cible (utile pour
    // probas globales de l'event).
    const avgWear = state.vehicles.reduce((s, v) => s + (v.wear || 0), 0) / state.vehicles.length;
    const fakeColor = avgWear < 30 ? 'green' : avgWear < 65 ? 'orange' : 'red';
    mult *= CONDITION_RISK_MULTIPLIER[fakeColor];
  }
  // 2) Staff garage
  const hired = state?.hiredSupport || {};
  if (hired['role-mehdi-ferhat']) mult *= 0.85;
  if (hired['role-bruno-lavergne']) mult *= 0.75;
  // 3) Fatigue chauffeur moyen
  if (Array.isArray(state?.drivers) && state.drivers.length > 0) {
    const avgFatigue = state.drivers.reduce((s, d) => s + (d.fatigue || 0), 0) / state.drivers.length;
    if (avgFatigue > 70) mult *= 1.15;
  }
  // 4) Booster de pannes (Canicule / Verglas continuer prudemment), expire
  // après `breakdownBoostUntil`. Multiplicateur ×1.25 pendant la fenêtre.
  // Ajouté 08/02/2026 — Q2 Lot B user prod.
  const today = state?.totalDays || 0;
  if ((state?.breakdownBoostUntil || 0) > today) {
    mult *= 1.25;
  }
  return mult;
}

/**
 * Coefficient d'absorption d'impact réputation par les fonctions support.
 * Renvoie un nombre dans [0, 0.75]. Appliqué via :
 *
 *   reputationFinale = repAnnoncée × (1 - absorption)
 *
 *   • role-thomas-vidal (Communication) → 0.10
 *   • role-aicha-diallo (Attachée presse + crisis_comm) → 0.15
 *   • role-stephanie-trouve (Commerciale) → 0.10
 *   • role-juliette-charron-berthet (Avocate-conseil) → 0.10
 *   • role-yanis-elkaroui (DirCom) → 0.20
 *   • role-mehdi-ferhat (mécano) → 0.05 (déjà sur place pour atténuer)
 *   • role-bruno-lavergne (chef atelier) → 0.05
 *
 * Le cap maximum est de 0.75 pour ne pas annuler complètement les pénalités.
 *
 * @param {object} state — Zustand state slice
 * @returns {number} absorption coefficient [0, 0.75]
 */
const ABSORPTION_PER_ROLE = {
  'role-thomas-vidal': 0.10,
  'role-aicha-diallo': 0.15,
  'role-yanis-elkaroui': 0.20,
  'role-stephanie-trouve': 0.10,
  'role-juliette-charron-berthet': 0.10,
  'role-mehdi-ferhat': 0.05,
  'role-bruno-lavergne': 0.05,
};

// 08/02/2026 — Q2 Lot C user prod : investissements sécurité/IT qui
// rassurent clients et employés.
//   inv-it-backup-01 (Sauvegarde + audit IT) : +0.05 absorption
//   inv-security-01 (Vidéosurveillance / gardiennage) : +0.05 absorption
const ABSORPTION_PER_INVESTMENT = {
  'inv-it-backup-01': 0.05,
  'inv-security-01': 0.05,
};

export function computeReputationAbsorption(state) {
  const hired = state?.hiredSupport || {};
  let abs = 0;
  for (const roleId of Object.keys(ABSORPTION_PER_ROLE)) {
    if (hired[roleId]) abs += ABSORPTION_PER_ROLE[roleId];
  }
  // Cumul investissements sécurité/IT (08/02/2026 — Q2 Lot C).
  const active = state?.activeInvestments || {};
  for (const invId of Object.keys(ABSORPTION_PER_INVESTMENT)) {
    if (active[invId]) abs += ABSORPTION_PER_INVESTMENT[invId];
  }
  return Math.min(0.75, abs);
}

/**
 * Applique l'absorption sur un delta de réputation négatif. Retourne un
 * entier (arrondi). Si delta >= 0, pass-through (bonus jamais réduit).
 */
export function applyReputationAbsorption(state, repDelta) {
  if (repDelta >= 0) return repDelta;
  const abs = computeReputationAbsorption(state);
  return Math.round(repDelta * (1 - abs));
}

/**
 * Détermine si le joueur a le staff nécessaire pour proposer le choix
 * « Équipe interne » (uniquement si Mehdi OU Bruno est embauché).
 */
export function hasInternalGarageTeam(state) {
  const hired = state?.hiredSupport || {};
  return !!(hired['role-mehdi-ferhat'] || hired['role-bruno-lavergne']);
}
